import { Tooltip } from 'antd'
import React, { useEffect, useState, useContext, useRef, useCallback } from 'react'
import { CheckIcon } from '@heroicons/react/solid'
import { InformationCircleIcon } from '@heroicons/react/outline'
import { useTranslation } from 'react-i18next'
import { Button } from './Button'
import { UserContext } from '../hooks/UserContext'

export const DropdownMenu = ({ options, selected, element, action, isSuperAdminOrAdmin, isWarehouseMode = false }) => {
  const { i18n } = useTranslation()
  const [showDropdown, setShowDropdown] = useState(false)
  const [initialState, setInitialState] = useState(selected)
  const [dropdownPosition, setDropdownPosition] = useState({ top: 0, left: 0 })
  const buttonRef = useRef(null)
  const dropdownRef = useRef(null)
  const { user } = useContext(UserContext)
  // Whether the current user may assign the elevated WH Admin role. Backend-driven
  // (UserSerializer#permissions); the server still enforces warehouse scope at write time.
  const canAssignWhAdmin = user?.permissions?.assign_wh_admin === true

  const updateDropdownPosition = useCallback(() => {
    if (buttonRef.current) {
      const rect = buttonRef.current.getBoundingClientRect()
      const spaceBelow = window.innerHeight - rect.bottom
      const dropdownHeight = 320 // matches max-h-80 (20rem = 320px)

      if (spaceBelow < dropdownHeight) {
        setDropdownPosition({ bottom: window.innerHeight - rect.top + 8, left: rect.left, openUp: true })
      } else {
        setDropdownPosition({ top: rect.bottom + 8, left: rect.left, openUp: false })
      }
    }
  }, [])

  const handleDropdownToggle = () => {
    if (!showDropdown) updateDropdownPosition()
    setShowDropdown(prevShowDropdown => !prevShowDropdown)
  }

  // An action that reports a failed save (returns false) gets the saved selection put back: a reload
  // that returns identical data keeps the same `selected` reference, so the effect below never fires.
  const handleConfirmButton = async () => {
    setShowDropdown(false)
    if ((await action(element, initialState)) === false) setInitialState(selected)
  }

  const handleCancelButton = event => {
    setInitialState(selected)
    setShowDropdown(false)
  }

  const handleOptionClick = option => {
    const optionKey = isWarehouseMode ? option.id : option.role

    if (initialState.includes(optionKey)) {
      setInitialState(prevInitialState => prevInitialState.filter(item => item !== optionKey))
    } else {
      setInitialState(prevInitialState => [...prevInitialState, optionKey])
    }
  }

  useEffect(() => {
    setInitialState(selected)
  }, [selected])

  useEffect(() => {
    if (!showDropdown) return
    const handleScroll = () => {
      updateDropdownPosition()
    }
    window.addEventListener('scroll', handleScroll, { capture: true })
    return () => {
      window.removeEventListener('scroll', handleScroll, { capture: true })
    }
  }, [showDropdown, updateDropdownPosition])

  const getDisplayText = () => {
    const count = initialState.length
    if (isWarehouseMode) {
      return count === 1 ? i18n.t('dropdown_menu.warehouse_associated_one', { count }) : i18n.t('dropdown_menu.warehouse_associated_other', { count })
    }
    return count === 1 ? i18n.t('dropdown_menu.role_assigned_one', { count }) : i18n.t('dropdown_menu.role_assigned_other', { count })
  }

  return (
    <div className="relative inline-block">
      <button
        ref={buttonRef}
        className={!isSuperAdminOrAdmin ? 'flex items-center text-black-500 focus:outline-none' : 'flex items-center text-gray-300 focus:outline-none'}
        onClick={handleDropdownToggle}
      >
        {getDisplayText()}
        {!isSuperAdminOrAdmin && (
          <svg className={`ml-2 w-5 h-5`} fill="none" stroke={'currentColor'} viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
          </svg>
        )}
      </button>
      {showDropdown && !isSuperAdminOrAdmin && (
        <div
          ref={dropdownRef}
          className="fixed border border-gray-200 rounded-lg shadow-xl bg-white"
          style={{
            ...(dropdownPosition.openUp
              ? { bottom: dropdownPosition.bottom, left: dropdownPosition.left }
              : { top: dropdownPosition.top, left: dropdownPosition.left }),
            width: '280px',
            zIndex: 9999,
          }}
        >
          <div className="p-2 max-h-80 overflow-y-auto">
            {options.map((option, index) => {
              const optionKey = isWarehouseMode ? option.id : option.role
              const isSelected = initialState.includes(optionKey)
              const isDisabled = !isWarehouseMode && (option.role === 'ROLE_OPERATOR' || (option.role === 'ROLE_WH_ADMIN' && !canAssignWhAdmin))

              return (
                <div
                  key={index}
                  className={`flex items-start p-2 rounded-lg cursor-pointer mb-1 ${
                    isDisabled
                      ? 'opacity-50 cursor-not-allowed bg-gray-100'
                      : isSelected
                        ? 'bg-blue-50 border border-blue-200'
                        : 'hover:bg-gray-50 border border-transparent'
                  }`}
                  onClick={() => !isDisabled && handleOptionClick(option)}
                >
                  <div
                    className={`flex-shrink-0 w-5 h-5 mt-0.5 mr-3 rounded border flex items-center justify-center ${
                      isSelected ? 'bg-blue-600 border-blue-600' : 'border-gray-300'
                    }`}
                  >
                    {isSelected && <CheckIcon className="w-3.5 h-3.5 text-white" />}
                  </div>
                  <div className="flex-1 min-w-0 flex items-center">
                    <div className={`text-sm font-semibold ${isSelected ? 'text-blue-700' : 'text-gray-900'}`}>{option.name}</div>
                    {!isWarehouseMode && option.description && (
                      <Tooltip placement="top" title={option.description} getPopupContainer={() => document.body} zIndex={10000}>
                        <InformationCircleIcon className="ml-2 w-4 h-4 flex-shrink-0 text-gray-400" />
                      </Tooltip>
                    )}
                  </div>
                </div>
              )
            })}
          </div>
          <div className="flex p-2 border-t border-gray-200 gap-2">
            <Button type="secondary" className="flex-1 px-3 py-1.5 text-sm text-gray-700 hover:text-gray-900" onClick={handleCancelButton}>
              {i18n.t('dropdown_menu.cancel')}
            </Button>
            <Button type="primary" className="flex-1 px-3 py-1.5 text-sm bg-blue-500 text-white rounded-lg hover:bg-blue-600" onClick={handleConfirmButton}>
              {i18n.t('dropdown_menu.confirm')}
            </Button>
          </div>
        </div>
      )}
    </div>
  )
}

export default DropdownMenu
